import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const biologyUnit5Notes: UnitNotes = {
  unitId: "unit-5",
  unitName: "Unit 5: Heredity",
  examWeight: "8–11%",
  focus: "Evolution (EVO), Information Storage and Transmission (IST), Systems Interactions (SYI)",
  sections: [
    {
      id: "topic-5-1",
      title: "Topic 5.1: Meiosis",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 The Purpose of Meiosis</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Meiosis is a specialized type of cell division that reduces the chromosome number by half, resulting in the production of <strong>haploid (<InlineMath math="n" />) gametes</strong> (sperm and egg) from <strong>diploid (<InlineMath math="2n" />) germ cells</strong>. This ensures that fertilization restores the correct diploid number in the zygote.
            </p>
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Diploid (<InlineMath math="2n" />)</h4>
                <p className="text-xs text-slate-700 mt-1">Two full sets of chromosomes (one maternal, one paternal). Humans: <InlineMath math="2n=46" />. Somatic cells.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Haploid (<InlineMath math="n" />)</h4>
                <p className="text-xs text-slate-700 mt-1">One single set of chromosomes. Humans: <InlineMath math="n=23" />. Gametes (sex cells).</p>
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">Homologous Pairs</h4>
                <p className="text-xs text-slate-700 mt-1">Chromosomes with same length and genes at same loci, but potentially different alleles.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Phases of Meiosis</h3>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2">Interphase (Before Meiosis)</h4>
                <p className="text-sm text-slate-700"><strong>G1, S, G2:</strong> Cell grows and DNA replicates during S phase. Chromosomes consist of two genetically identical <strong>sister chromatids</strong>.</p>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-indigo-900">Meiosis I: Reductional Division</h4>
                <p className="text-xs text-slate-600 mb-3">Reduces cell from <InlineMath math="2n" /> to <InlineMath math="n" /> by separating homologous chromosomes.</p>
                <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
                  <li><strong>Prophase I:</strong> Chromatin condenses. <strong>Synapsis</strong> occurs: homologous chromosomes pair into a <strong>tetrad</strong>. <strong>Crossing Over</strong> happens at <strong>chiasmata</strong>.</li>
                  <li><strong>Metaphase I:</strong> Tetrads align at plate. <strong>Independent Assortment</strong>: random orientation of pairs.</li>
                  <li><strong>Anaphase I:</strong> Spindle fibers pull <strong>homologous chromosomes</strong> apart. Sister chromatids remain attached.</li>
                  <li><strong>Telophase I/Cytokinesis:</strong> Two haploid cells form; each chromosome still has two sister chromatids.</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">Meiosis II: Equational Division</h4>
                <p className="text-xs text-slate-600 mb-3">Separates sister chromatids; mechanically similar to mitosis.</p>
                <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
                  <li><strong>Prophase II:</strong> Spindle forms; no DNA replication occurs before this.</li>
                  <li><strong>Metaphase II:</strong> Chromosomes align individually. Chromatids are <em>not</em> identical due to crossing over.</li>
                  <li><strong>Anaphase II:</strong> Centromeres split; sister chromatids move to opposite poles.</li>
                  <li><strong>Telophase II/Cytokinesis:</strong> Nuclei reform. <strong>Result:</strong> 4 unique haploid daughter cells.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Mitosis vs. Meiosis Summary</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left border-collapse border border-slate-200">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="p-2 border border-slate-200">Feature</th>
                    <th className="p-2 border border-slate-200">Mitosis</th>
                    <th className="p-2 border border-slate-200">Meiosis</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <tr>
                    <td className="p-2 border border-slate-200 font-semibold">Rounds of Division</td>
                    <td className="p-2 border border-slate-200">1</td>
                    <td className="p-2 border border-slate-200">2</td>
                  </tr>
                  <tr>
                    <td className="p-2 border border-slate-200 font-semibold">Daughter Cells</td>
                    <td className="p-2 border border-slate-200">2 Genetically Identical</td>
                    <td className="p-2 border border-slate-200">4 Genetically Unique</td>
                  </tr>
                  <tr>
                    <td className="p-2 border border-slate-200 font-semibold">Chromosome Content</td>
                    <td className="p-2 border border-slate-200"><InlineMath math="2n \to 2n" /></td>
                    <td className="p-2 border border-slate-200"><InlineMath math="2n \to n" /></td>
                  </tr>
                  <tr>
                    <td className="p-2 border border-slate-200 font-semibold">Role</td>
                    <td className="p-2 border border-slate-200">Growth, Tissue Repair</td>
                    <td className="p-2 border border-slate-200">Sexual Reproduction</td>
                  </tr>
                  <tr>
                    <td className="p-2 border border-slate-200 font-semibold">Synapsis/Crossing Over</td>
                    <td className="p-2 border border-slate-200">No</td>
                    <td className="p-2 border border-slate-200">Yes (Prophase I)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-2",
      title: "Topic 5.2: Meiosis and Genetic Diversity",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✨ Mechanisms of Variation</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">1. Crossing Over (Recombination)</h4>
                <p className="text-sm text-slate-700 mt-1">During Prophase I, non-sister chromatids exchange DNA segments. This produces <strong>recombinant chromosomes</strong> and disrupts linked genes.</p>
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">2. Independent Assortment</h4>
                <p className="text-sm text-slate-700 mt-1">During Metaphase I, orientation of maternal/paternal chromosomes is random.</p>
                <div className="mt-2 bg-white p-2 rounded border border-emerald-100">
                  <p className="text-xs text-slate-600"><strong>Calculation:</strong> <InlineMath math="2^n" /> combinations (where <InlineMath math="n" /> is haploid number).</p>
                  <p className="text-xs text-slate-600">Humans: <InlineMath math="2^{23} \approx 8.4 \text{ million}" /> possible gamete combinations.</p>
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">3. Random Fertilization</h4>
                <p className="text-sm text-slate-700 mt-1">Any sperm can fertilize any egg. <InlineMath math="8.4 \text{m} \times 8.4 \text{m} = 70 \text{ trillion}" /> unique zygote combinations.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Significance to Evolution</h3>
            <p className="text-slate-700 leading-relaxed">
              Genetic diversity provides the raw material for <strong>Natural Selection</strong>. A diverse population is more likely to contain individuals with alleles that provide a survival advantage during environmental shifts.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Chromosomal Disorders</h3>
            <KeyTerm 
              term="Nondisjunction" 
              definition="The failure of chromosomes to separate correctly during Meiosis I (homologs) or Meiosis II (sister chromatids)."
            />
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mt-3">
              <h4 className="font-bold text-red-900">Result: Aneuploidy</h4>
              <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                <li><strong>Trisomy (n+1):</strong> Three copies of a chromosome (e.g., Down Syndrome/Trisomy 21).</li>
                <li><strong>Monosomy (n-1):</strong> One copy of a chromosome.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-3",
      title: "Topic 5.3: Mendelian Genetics",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📜 Mendel’s Laws</h3>
            <div className="space-y-3">
              <div className="p-4 bg-slate-50 rounded-lg border-l-4 border-slate-400">
                <h4 className="font-bold text-slate-900">Law of Segregation</h4>
                <p className="text-sm text-slate-700">Alleles separate during gamete formation so each gamete receives only one. <em>Connection:</em> Anaphase I separation of homologs.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border-l-4 border-slate-400">
                <h4 className="font-bold text-slate-900">Law of Independent Assortment</h4>
                <p className="text-sm text-slate-700">Genes for different traits segregate independently. <em>Note:</em> Only applies to unlinked genes. <em>Connection:</em> Metaphase I random alignment.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧮 Probability in Genetics</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Multiplication Rule ("AND")</h4>
                <p className="text-xs text-slate-600 mb-2">Probability of independent events occurring together.</p>
                <div className="bg-white p-2 rounded text-xs">
                  <p>Ex: <InlineMath math="Aa \times Aa \to aa" /></p>
                  <BlockMath math="0.5(a) \times 0.5(a) = 0.25" />
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Addition Rule ("OR")</h4>
                <p className="text-xs text-slate-600 mb-2">Probability of mutually exclusive events.</p>
                <div className="bg-white p-2 rounded text-xs">
                  <p>Ex: <InlineMath math="Aa \times Aa \to Aa" /></p>
                  <BlockMath math="(0.5 \times 0.5) + (0.5 \times 0.5) = 0.50" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧪 Analysis of Crosses</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-slate-900 text-sm">Monohybrid</h5>
                <p className="text-xs text-slate-600">Heterozygote cross (<InlineMath math="Pp \times Pp" />).<br/>Phenotype 3:1<br/>Genotype 1:2:1</p>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-slate-900 text-sm">Dihybrid</h5>
                <p className="text-xs text-slate-600">Two unlinked traits.<br/>Phenotype 9:3:3:1</p>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-slate-900 text-sm">Test Cross</h5>
                <p className="text-xs text-slate-600">Dominant phenotype (<InlineMath math="A?" />) crossed with <InlineMath math="aa" /> to find genotype.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Chi-Square ($\chi^2$) Goodness of Fit</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="\chi^2 = \sum \frac{(O - E)^2}{E}" />
              <ul className="list-disc pl-5 mt-4 text-sm text-slate-700 space-y-2">
                <li><strong>Null Hypothesis (<InlineMath math="H_0" />):</strong> No significant difference; any variance is due to chance.</li>
                <li><strong>Degrees of Freedom (df):</strong> <InlineMath math="n - 1" /> categories.</li>
                <li><strong>Interpretation:</strong>
                  <ul className="list-circle pl-5 mt-1">
                    <li>If <InlineMath math="\chi^2 > \text{Critical Value}" />: <strong>Reject <InlineMath math="H_0" /></strong> (Significant difference).</li>
                    <li>If <InlineMath math="\chi^2 < \text{Critical Value}" />: <strong>Fail to Reject <InlineMath math="H_0" /></strong> (Data fits hypothesis).</li>
                  </ul>
                </li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-4",
      title: "Topic 5.4: Non-Mendelian Genetics",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔗 Linked Genes</h3>
            <p className="text-sm text-slate-700 mb-3">Genes on the same chromosome that tend to be inherited together.</p>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <h4 className="font-bold text-indigo-900">Map Units (Centimorgans)</h4>
              <p className="text-sm text-slate-700 mt-1">1 Map Unit = 1% Recombination Frequency.</p>
              <div className="mt-2 text-xs font-mono text-indigo-800">
                Freq = (Recombinants / Total Offspring) × 100
              </div>
              <p className="text-xs text-slate-600 mt-2">If frequency &lt; 50%, genes are linked. If ≈ 50%, they assort independently.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 Sex-Linked and Non-Nuclear</h3>
            <div className="space-y-3">
              <div className="bg-pink-50 p-4 rounded-lg border-l-4 border-pink-400">
                <h4 className="font-bold text-pink-900">X-Linked Recessive</h4>
                <p className="text-sm text-slate-700">Males are <strong>hemizygous</strong> (only one X). Affects mostly males; mothers pass to sons. Ex: Hemophilia.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">Non-Nuclear Inheritance</h4>
                <p className="text-sm text-slate-700">Mitochondria and chloroplasts have their own DNA. Inherited <strong>maternally</strong> via the egg.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎨 Patterns of Complexity</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-sm">Incomplete Dominance</h5>
                <p className="text-xs text-slate-600">Blend of phenotypes (Red × White = Pink).</p>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-sm">Codominance</h5>
                <p className="text-xs text-slate-600">Both alleles distinct (Blood type AB).</p>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-sm">Multiple Alleles</h5>
                <p className="text-xs text-slate-600">More than two alleles exist (ABO blood group).</p>
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <h5 className="font-bold text-sm">Polygenic Inheritance</h5>
                <p className="text-xs text-slate-600">Many genes affect one trait (Height, Skin color).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-5",
      title: "Topic 5.5: Environmental Effects on Phenotype",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌱 Phenotypic Plasticity</h3>
            <KeyTerm 
              term="Phenotypic Plasticity" 
              definition="The ability of one genotype to produce more than one phenotype when exposed to different environments."
            />
            <p className="font-semibold text-slate-800 mt-4 mb-2">Examples of Interaction (Genotype + Environment):</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">Soil pH</h4>
                <p className="text-xs text-slate-700 mt-1">Hydrangeas change color (blue vs pink) based on soil acidity and aluminum availability.</p>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h4 className="font-bold text-cyan-900">Temperature</h4>
                <p className="text-xs text-slate-700 mt-1">Reptile sex determination is based on egg incubation temperature; Siamese cat pigment only develops on cooler extremities.</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">UV Exposure</h4>
                <p className="text-xs text-slate-700 mt-1">Tanning in humans is an environmental response to protect DNA from UV damage.</p>
              </div>
              <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                <h4 className="font-bold text-rose-900">Nutrients/Diet</h4>
                <p className="text-xs text-slate-700 mt-1">Organism size and health depend heavily on nutrient availability despite genetic potential.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default biologyUnit5Notes;